import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { getLscAdminHealth, getLscAdminIssues } from '../api';
import type { LscAdminHealth, LscAdminSupportIssue } from '../types';
import LoadingState from '../components/LoadingState';
import { lscPath } from '../routing';
import { parseAdminIssueError } from '../adminIssueCopy';
import {
  adminHealthReleaseItems,
  adminHealthSafetyLockItems,
  adminHealthSmokeBaselineLabel,
  adminHealthWarningDetail,
  adminHealthWarningLabel,
  ADMIN_HEALTH_FORBIDDEN_CONTROL_LABELS,
  formatAdminHealthBoolean,
  formatAdminHealthValue,
  LSC_ADMIN_HEALTH_FRONTEND_NOTE,
  LSC_ADMIN_HEALTH_INTRO,
  LSC_ADMIN_HEALTH_NO_MUTATION_NOTE,
  LSC_ADMIN_HEALTH_READINESS_NOTE,
  LSC_ADMIN_HEALTH_SMOKE_NOTE,
  LSC_ADMIN_HEALTH_TITLE,
  parseAdminHealthError,
} from '../adminHealthCopy';

function HealthCard({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card space-y-3 text-sm" data-testid={testId}>
      <h3 className="font-semibold text-white">{title}</h3>
      {children}
    </section>
  );
}

function HealthField({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex justify-between gap-4 text-secondary-400">
      <dt>{label}</dt>
      <dd className="text-right text-secondary-200 break-all">{formatAdminHealthValue(value)}</dd>
    </div>
  );
}

export function LscAdminPageView({
  issues,
  health,
  loading,
  error,
  healthError,
}: {
  issues: LscAdminSupportIssue[];
  health: LscAdminHealth | null;
  loading: boolean;
  error: string | null;
  healthError: string | null;
}) {
  if (loading) return <LoadingState message="Loading admin overview..." />;

  const openCount = issues.filter((issue) => issue.status === 'open').length;
  const launchBlockerCount = issues.filter((issue) => issue.severity === 'launch_blocker').length;
  const releaseItems = adminHealthReleaseItems(health?.admin_release);
  const safetyItems = adminHealthSafetyLockItems(health?.safety_locks);

  return (
    <div className="space-y-6" data-testid="lsc-admin-page">
      {error ? (
        <p className="text-sm text-red-300" role="alert" data-testid="lsc-admin-page-error">
          {error}
        </p>
      ) : null}

      {healthError ? (
        <p className="text-sm text-amber-300" role="status" data-testid="lsc-admin-health-error">
          {healthError}
        </p>
      ) : null}

      {health ? (
        <div className="space-y-4" data-testid="lsc-admin-production-health">
          <div>
            <h2 className="text-lg font-semibold text-white">{LSC_ADMIN_HEALTH_TITLE}</h2>
            <p className="text-sm text-secondary-400 mt-1">{LSC_ADMIN_HEALTH_INTRO}</p>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <HealthCard title="Production identity" testId="lsc-admin-health-identity-card">
              <dl className="grid gap-2">
                <HealthField label="Backend SHA" value={health.backend?.git_sha} />
                <HealthField label="Backend revision" value={health.backend?.revision} />
                <HealthField label="Backend service" value={health.backend?.service} />
                <HealthField label="Backend status" value={health.backend?.status} />
              </dl>
              <p className="text-xs text-secondary-500">{LSC_ADMIN_HEALTH_FRONTEND_NOTE}</p>
            </HealthCard>

            <HealthCard title="Billing posture" testId="lsc-admin-health-billing-card">
              <dl className="grid gap-2">
                <HealthField
                  label="Billing model"
                  value={formatAdminHealthBoolean(health.billing?.billing_model_enabled)}
                />
                <HealthField
                  label="Checkout"
                  value={formatAdminHealthBoolean(health.billing?.checkout_enabled)}
                />
                <HealthField
                  label="Enforcement"
                  value={formatAdminHealthBoolean(health.billing?.enforcement_enabled)}
                />
              </dl>
            </HealthCard>

            <HealthCard title="Automation bridge" testId="lsc-admin-health-bridge-card">
              <dl className="grid gap-2">
                <HealthField
                  label="Automation bridge"
                  value={formatAdminHealthBoolean(
                    health.bridge?.paid_beta_automation_entitlement_enabled
                  )}
                />
              </dl>
            </HealthCard>

            <HealthCard title="Smoke status and known warnings" testId="lsc-admin-health-smoke-card">
              <dl className="grid gap-2 mb-3">
                <HealthField
                  label="Accepted baseline"
                  value={adminHealthSmokeBaselineLabel(health.smoke?.baseline_accepted)}
                />
                <HealthField label="Last verified" value={health.smoke?.last_verified_at} />
              </dl>
              <p className="text-xs text-secondary-500">{LSC_ADMIN_HEALTH_SMOKE_NOTE}</p>
              {health.smoke?.note ? (
                <p className="text-xs text-secondary-500">{health.smoke.note}</p>
              ) : null}
              <ul className="space-y-2 mt-3">
                {(health.known_warnings ?? []).map((warning) => (
                  <li
                    key={warning.id}
                    className="rounded border border-amber-900/50 bg-amber-950/20 p-2"
                    data-testid="lsc-admin-health-known-warning"
                  >
                    <p className="text-amber-200">{adminHealthWarningLabel(warning)}</p>
                    <p className="text-secondary-400 text-xs mt-1">
                      {adminHealthWarningDetail(warning)}
                    </p>
                    <p className="text-secondary-500 text-xs mt-1">
                      Customer visible: {warning.customer_visible ? 'Yes' : 'No'}
                    </p>
                  </li>
                ))}
              </ul>
            </HealthCard>

            <HealthCard title="Admin release readiness" testId="lsc-admin-health-release-card">
              <ul className="space-y-2">
                {releaseItems.map((item) => (
                  <li key={item.key} className="flex justify-between gap-4 text-secondary-300">
                    <span>{item.label}</span>
                    <span>{item.ready ? 'Ready' : 'Missing'}</span>
                  </li>
                ))}
              </ul>
            </HealthCard>

            <HealthCard title="Launch readiness markers" testId="lsc-admin-health-readiness-card">
              <dl className="grid gap-2">
                <HealthField
                  label="Expanded readiness"
                  value={health.launch_readiness?.expanded_readiness_marker}
                />
                <HealthField
                  label="Original readiness"
                  value={health.launch_readiness?.original_readiness_marker}
                />
                <HealthField
                  label="Dynamic calculation"
                  value={health.launch_readiness?.dynamic_calculation ? 'Yes' : 'No'}
                />
              </dl>
              <p className="text-xs text-secondary-500">{LSC_ADMIN_HEALTH_READINESS_NOTE}</p>
            </HealthCard>

            <HealthCard title="Safety locks" testId="lsc-admin-health-safety-card">
              <ul className="space-y-2">
                {safetyItems.map((item) => (
                  <li key={item.key} className="flex justify-between gap-4 text-secondary-300">
                    <span>{item.label}</span>
                    <span>{item.locked ? 'Locked' : 'Unavailable'}</span>
                  </li>
                ))}
              </ul>
            </HealthCard>

            <HealthCard title="Deferred verification" testId="lsc-admin-health-deferred-card">
              {(health.deferred_verification ?? []).length === 0 ? (
                <p className="text-secondary-500">No deferred verification markers.</p>
              ) : (
                <ul className="space-y-2">
                  {health.deferred_verification?.map((marker) => (
                    <li
                      key={marker}
                      className="rounded border border-secondary-800 p-2 text-secondary-300 break-all"
                      data-testid="lsc-admin-health-deferred-marker"
                    >
                      {marker}
                    </li>
                  ))}
                </ul>
              )}
            </HealthCard>
          </div>

          <p className="text-xs text-secondary-600">{LSC_ADMIN_HEALTH_NO_MUTATION_NOTE}</p>
          <div className="hidden" data-testid="lsc-admin-health-forbidden-controls">
            {ADMIN_HEALTH_FORBIDDEN_CONTROL_LABELS.join(', ')}
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="card space-y-2" data-testid="lsc-admin-summary-open">
          <p className="text-xs uppercase tracking-wide text-secondary-500">Open issues</p>
          <p className="text-3xl font-semibold text-white">{openCount}</p>
        </div>
        <div className="card space-y-2" data-testid="lsc-admin-summary-launch-blocker">
          <p className="text-xs uppercase tracking-wide text-secondary-500">Launch blockers</p>
          <p className="text-3xl font-semibold text-white">{launchBlockerCount}</p>
        </div>
      </div>

      <div className="card space-y-3">
        <h2 className="font-semibold text-white">Support triage</h2>
        <p className="text-sm text-secondary-400">
          Review submitted customer reports, update triage status, and leave internal or customer-visible
          notes.
        </p>
        <Link
          to={lscPath('/admin/issues')}
          className="inline-flex btn-primary text-sm"
          data-testid="lsc-admin-open-issue-queue"
        >
          Open issue queue
        </Link>
        <Link
          to={lscPath('/admin/autopublish')}
          className="inline-flex text-sm text-primary-300"
          data-testid="lsc-admin-open-autopublish-nightly"
        >
          Autopublish nightly report
        </Link>
      </div>
    </div>
  );
}

const LscAdminPage: React.FC = () => {
  const [issues, setIssues] = useState<LscAdminSupportIssue[]>([]);
  const [health, setHealth] = useState<LscAdminHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setHealthError(null);

    void Promise.allSettled([getLscAdminIssues(), getLscAdminHealth()])
      .then(([issuesResult, healthResult]) => {
        if (cancelled) return;

        if (issuesResult.status === 'fulfilled') {
          setIssues(issuesResult.value);
        } else {
          setError(parseAdminIssueError(issuesResult.reason));
        }

        if (healthResult.status === 'fulfilled') {
          setHealth(healthResult.value);
        } else {
          setHealthError(parseAdminHealthError(healthResult.reason));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <LscAdminPageView
      issues={issues}
      health={health}
      loading={loading}
      error={error}
      healthError={healthError}
    />
  );
};

export default LscAdminPage;
